import { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { debounce } from 'throttle-debounce';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { Input } from '@/components/ui/input';
import { logger } from '@/lib/logger';
import { SearchSportActivitiesDocument } from '~/graphql/generated/graphql';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { ExercisePicker } from '@/lib/local-api/sport-activity/exercise-picker';
import type { SportActivityRecord } from '~/powersync/generated/schema';

/**
 * The search input's derived state, emitted to the parent modal which owns the
 * results-area view (loading skeleton / error / no-results / list). Ordering
 * happens here (relevance order from the search index); filtering and rendering
 * happen in the modal.
 */
export interface SportActivitySearchState {
  results: SportActivityRecord[];
  isLoading: boolean;
  hasError: boolean;
}

export interface SportActivitySelectorSearchProps {
  /** Called whenever the search results or their loading/error status change. */
  onStateChange?: (state: SportActivitySearchState) => void;
}

export const SportActivitySelectorSearch = ({
  onStateChange,
}: SportActivitySelectorSearchProps) => {
  const { t } = useTranslation(['workout', 'common']);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  // Debounced search: only issue a request once the user pauses typing (500ms),
  // instead of on every keystroke, to avoid redundant network round-trips.
  const debounceSearch = useMemo(
    () =>
      debounce(500, (value: string) => {
        setDebouncedSearchValue(value);
      }),
    [],
  );

  // Push each keystroke into the debouncer, and cancel any pending call on unmount.
  useEffect(() => {
    debounceSearch(searchValue);
  }, [searchValue, debounceSearch]);

  useEffect(() => () => debounceSearch.cancel(), [debounceSearch]);

  // GraphQL query to get sport activity IDs (relevance-ordered by the search index)
  const {
    data: searchData,
    loading: isSearching,
    error: searchError,
  } = useQuery(SearchSportActivitiesDocument, {
    variables: {
      query: {
        fulltextSearch: debouncedSearchValue || undefined,
        limit: 50,
      },
    },
  });

  // Extract IDs from search results
  const sportActivityIds = useMemo(
    () => searchData?.sportActivities?.items?.map((item) => item.id) ?? [],
    [searchData],
  );

  // Clean local-api query to get full sport activity records
  const {
    data: sportActivities,
    isLoading: isLoadingFromPowerSync,
    error: powerSyncError,
  } = useLivePowerSyncQuery<SportActivityRecord>(
    db.SportActivity.queries.findByIds(sportActivityIds),
    [sportActivityIds],
    {
      enabled: Boolean(searchData),
    },
  );

  // Records reordered into the server's relevance order, dropping ids that
  // didn't hydrate locally.
  const results = useMemo(
    () => ExercisePicker.orderByIds(sportActivityIds, sportActivities ?? []),
    [sportActivityIds, sportActivities],
  );

  const isLoading = isSearching || isLoadingFromPowerSync;
  const hasError = Boolean(searchError || powerSyncError);

  // Notify parent about results + loading/error status changes
  useEffect(() => {
    onStateChange?.({ results, isLoading, hasError });
  }, [results, isLoading, hasError, onStateChange]);

  // Log any errors
  useEffect(() => {
    if (searchError) {
      logger.error(searchError, 'SportActivitySearch: GraphQL search error');
    }
    if (powerSyncError) {
      logger.error(powerSyncError, 'SportActivitySearch: PowerSync error');
    }
  }, [searchError, powerSyncError]);

  return (
    <View>
      <Input
        testID="exercise-search-input"
        placeholder={t('sportActivitySelector.searchPlaceholder')}
        value={searchValue}
        onChangeText={setSearchValue}
        className="mb-2"
      />
    </View>
  );
};
